import { useEffect, useState, useCallback } from 'react';
import { Embed, models, Report, service } from 'powerbi-client';
import { PowerBIEmbed } from 'powerbi-client-react';
import { TokenService } from 'core/services/TokenService';
import { setReport as setReportEmbed } from 'core/services/PBIReportService';
import { toastOpen } from 'core/slices/toasts';
import { TOAST_FAIL } from 'components/Toaster/Toaster';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'core/store';
import { envSettings } from './envSettings';
import './PowerBiReport.css';

type reportState = 'loading' | 'rendered' | 'loaded' | 'error';

export const PowerBiReport = ({
  reportId,
  reportToken,
  tokenService,
  onReportReset,
  onDataPointSelected,
}: {
  reportId: string;
  reportToken: string;
  tokenService: TokenService;
  onReportReset?: () => void;
  onBookmarkSave?: (name: string, state: string) => void;
  onDataPointSelected?: () => void;
}) => {
  const [report, setReport] = useState<Report | undefined>(undefined);
  const [reportState, setReportState] = useState<reportState>('loading');
  const dispatch = useDispatch<AppDispatch>();
  const searchParams = new URLSearchParams(window.location.search);
  const bookmarkState = searchParams.get('bookmarkState') || null;

  // Create a new configuration object when reportId or token changes
  const [reportConfig, setReportConfig] =
    useState<models.IReportEmbedConfiguration>(() => ({
      type: 'report',
      embedUrl: `${envSettings.url}${reportId}`,
      accessToken: reportToken,
      tokenType: envSettings.tokenType,
      settings: {
        personalBookmarksEnabled: envSettings.personalBookmarksEnabled,
        bars: envSettings.bars,
      },
    }));

  // Safe report reload function with error handling
  const safeReload = useCallback(async () => {
    if (!report) return;

    try {
      setReportState('loading');
      await report.reload();
    } catch (err) {
      dispatch(
        toastOpen({
          className: TOAST_FAIL,
          text: 'PowerBI report reload failed:' + err,
        })
      );
      setReportState('error');
    }
  }, [dispatch, report]);

  // Reset report function with error handling
  const resetReport = useCallback(() => {
    try {
      // If there is a bookmark applied, we need to remove it before resetting the report
      if (bookmarkState) {
        setReportConfig(prevConfig => ({
          ...prevConfig,
          bookmark: undefined,
        }));
      }

      if (onReportReset) {
        onReportReset();
      }

      // If there is a bookmark applied, the onReportReset will already reload the report
      // Reloading it again causes a race condition
      if (!bookmarkState && report) {
        safeReload();
      }
    } catch (err) {
      dispatch(
        toastOpen({
          className: TOAST_FAIL,
          text: 'Error resetting report:' + err,
        })
      );
    }
  }, [bookmarkState, onReportReset, report, safeReload, dispatch]);

  // Update configuration when reportId or token changes
  useEffect(() => {
    setReportState('loading');

    if (reportToken !== '' && reportId) {
      const isValid = tokenService.validateTokenReportPair(
        reportId,
        reportToken
      );

      setReportConfig({
        type: 'report',
        embedUrl: `${envSettings.url}${reportId}`,
        accessToken: isValid ? reportToken : '',
        tokenType: envSettings.tokenType,
        settings: {
          personalBookmarksEnabled: envSettings.personalBookmarksEnabled,
          bars: envSettings.bars,
        },
        bookmark:
          bookmarkState && isValid ? { state: bookmarkState } : undefined,
      });
    }
  }, [reportId, reportToken, tokenService, bookmarkState]);

  // Handle bookmark state changes
  useEffect(() => {
    if (report && bookmarkState && reportState === 'rendered') {
      try {
        report.bookmarksManager.applyState(bookmarkState).catch(err => {
          dispatch(
            toastOpen({
              className: TOAST_FAIL,
              text: 'Failed to apply bookmark state' + err,
            })
          );
        });
      } catch (err) {
        dispatch(
          toastOpen({
            className: TOAST_FAIL,
            text: 'Error applying bookmark state:' + err,
          })
        );
      }
    }
  }, [bookmarkState, dispatch, report, reportState]);

  // Handle report reset when bookmarkState is cleared
  useEffect(() => {
    if (report && !bookmarkState) {
      resetReport();
    }
  }, [report, bookmarkState, resetReport]);

  // Create event handlers with error handling
  const eventHandlersMap = new Map([
    [
      'rendered',
      () => {
        setReportState('rendered');
      },
    ],
    [
      'loaded',
      () => {
        setReportState('loaded');
      },
    ],
    [
      'error',
      (event?: service.ICustomEvent<any>) => {
        dispatch(
          toastOpen({
            className: TOAST_FAIL,
            text: 'PowerBI report error:' + event?.detail.detailedMessage,
          })
        );

        // Only set error state for critical errors that prevent report rendering
        const errorDetail = event?.detail;

        // Ignore these specific errors that don't affect functionality
        const ignoredErrors = [
          'Invoked filter serialization function with no filter.',
          'visualClickedFailed',
        ];

        if (
          errorDetail &&
          errorDetail.message &&
          !ignoredErrors.includes(errorDetail.message) &&
          errorDetail.level === 3
        ) {
          dispatch(
            toastOpen({
              className: TOAST_FAIL,
              text: 'An error occurred with the report. Please try again.',
            })
          );
          setReportState('error');
        }
      },
    ],
    [
      'dataSelected',
      (event?: service.ICustomEvent<any>) => {
        if (event && onDataPointSelected) {
          try {
            onDataPointSelected();
          } catch (err) {
            dispatch(
              toastOpen({
                className: TOAST_FAIL,
                text: 'Error in data point selection handler:' + err,
              })
            );
          }
        }
      },
    ],
  ]);

  const handleReportBI = useCallback(
    (embedObject: Embed) => {
      try {
        const reportObject = embedObject as Report;
        setReport(reportObject);
        dispatch(setReportEmbed(reportObject));
      } catch (err) {
        dispatch(
          toastOpen({
            className: TOAST_FAIL,
            text: 'Error handling report object:' + err,
          })
        );
      }
    },
    [dispatch]
  );

  return (
    <PowerBIEmbed
      key={reportId}
      embedConfig={reportConfig}
      eventHandlers={eventHandlersMap}
      cssClassName="reportContainer"
      getEmbeddedComponent={handleReportBI}
    />
  );
};
